
///
/// EarthBrowser - Email Verification Reminder Dialog
///
/// Created: 2025-11-21
/// Copyright (c) 2025 Geodata Labs
///

import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogTitle } from "@/components/ui/dialog"
import { CheckCircle2, Clock, Mail } from "lucide-react"

interface EmailVerificationDialogProps {
	open: boolean
	onClose: () => void
	email: string
}

export function EmailVerificationDialog({ open, onClose, email }: EmailVerificationDialogProps) {
	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
			<DialogPortal>
				<DialogOverlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
				<DialogContent className="overflow-hidden p-0 max-w-md rounded-2xl" aria-describedby="verification-description">
					<div className="relative bg-background/95 backdrop-blur-xl border border-border/50 shadow-2xl p-8">
						{/* Icon */}
						<div className="flex justify-center mb-6">
							<div className="relative">
								<div className="absolute inset-0 bg-blue-500/20 rounded-full blur-xl animate-pulse" />
								<div className="relative bg-blue-500/10 p-4 rounded-full border border-blue-500/30">
									<Mail className="h-12 w-12 text-blue-500" />
								</div>
							</div>
						</div>

						<DialogTitle className="text-2xl font-bold text-center mb-4 bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 bg-clip-text text-transparent">
							Check Your Email!
						</DialogTitle>

						<DialogDescription id="verification-description" className="text-center text-muted-foreground mb-6">
							We&apos;ve sent a verification email to:
							<span className="block mt-2 font-semibold text-foreground">
								{email}
							</span>
						</DialogDescription>

						{/* Instructions */}
						<div className="space-y-4 mb-6">
							<div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
								<CheckCircle2 className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
								<div className="text-sm text-blue-900 dark:text-blue-100">
									<strong>Step 1:</strong> Open your email inbox
								</div>
							</div>
							<div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
								<CheckCircle2 className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
								<div className="text-sm text-blue-900 dark:text-blue-100">
									<strong>Step 2:</strong> Click the verification link
								</div>
							</div>
							<div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
								<CheckCircle2 className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
								<div className="text-sm text-blue-900 dark:text-blue-100">
									<strong>Step 3:</strong> Start exploring EarthBrowser!
								</div>
							</div>
						</div>

						{/* Note */}
						<div className="flex items-start gap-2 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg mb-6">
							<Clock className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
							<div className="text-sm text-gray-700 dark:text-gray-300">
								<strong className="block mb-1">Didn&apos;t receive the email?</strong>
								Check your spam folder or wait a few minutes and try again.
								The verification link expires in 24 hours.
							</div>
						</div>

						{/* Close button */}
						<Button
							onClick={onClose}
							className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
						>
							Got it!
						</Button>
					</div>
				</DialogContent>
			</DialogPortal>
		</Dialog>
	)
}
